import {
	Box,
	Button,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';
import SearchForm from 'features/achievements/SearchForm';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Placeholder, SortableTableHeader } from 'components';
import AchievementItem from 'features/achievements/AchievementItem';
import AchievementToolbar from './AchievementToolbar';

import { useAlerts, useNavigation, useAchievements, useSelected } from 'hooks';

const TABLE_HEADERS = [
	{
		value: 'user_id',
		label: 'User',
		sortable: true,
		hidden: false
	},
	{
		value: 'reward',
		label: 'Reward',
		sortable: true,
		hidden: false
	},
	{
		value: 'points_achieved',
		label: 'Points Achieved',
		sortable: true,
		hidden: false
	},
	{
		value: 'status',
		label: 'Status',
		sortable: true,
		hidden: false
	},
	{
		value: 'created_at',
		label: 'Created On',
		sortable: true,
		hidden: false
	}
];

const AchievementList = () => {
	const navigate = useNavigate();

	const {
		isLoading,
		isEmpty,
		achievements,
		findAchievements,
		deleteAchievement,
		paginate,
		query,
		page,
		numPages,
		reloadAchievements,
		sortKey,
		sortDirection,
		handleSort
	} = useAchievements({});

	const searchParams = React.useMemo(
		() => ({
			'achievements.status': 'new'
		}),
		[]
	);

	const { handleClick, handleEditClick, handleAddClick } = useNavigation({
		history,
		url: '/achievements'
	});

	const { selectedIds, handleSelect, selected, handleClear, totalSelected } =
		useSelected();

	const { showAlertSuccess } = useAlerts();

	const handleDeleteClick = async (achievement) => {
		await deleteAchievement(achievement);
		showAlertSuccess('Achievement has been deleted');
		reloadAchievements();
	};

	React.useEffect(() => {
		findAchievements(
			{
				...searchParams,
				sort_key: sortKey,
				sort_direction: sortDirection
			},
			page
		);
	}, [searchParams, page, sortKey, sortDirection]);

	return (
		<>
			<Container maxWidth="xl" className="custom-Mul-Container">
				<>
					<Paper>
						<SearchForm
							isLoading={isLoading}
							query={query}
							handleSearch={findAchievements}
						/>
						{!isEmpty ? (
							<TableContainer component={Paper} elevation={0}>
								<Table>
									<TableHead>
										<TableRow>
											{TABLE_HEADERS.filter((h) => !h.hidden).map(
												(header, idx) => (
													<SortableTableHeader
														key={idx}
														handleSort={handleSort}
														value={`achievements.${header.value}`}
														sortable={header.sortable}
														sortKey={sortKey}
														sortDirection={sortDirection}
													>
														{header.label}
													</SortableTableHeader>
												)
											)}
											<TableCell></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{achievements.map((achievement, idx) => (
											<AchievementItem
												key={idx}
												achievement={achievement}
												isSelectable={false}
												selectedIds={selectedIds}
												handleSelect={handleSelect}
												handleClick={handleClick}
												handleDeleteClick={handleDeleteClick}
												handleEditClick={handleEditClick}
											/>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Placeholder
								icon={<Search />}
								title="No Achievements"
								subtitle="Please try another search"
							/>
						)}
					</Paper>
					{!isEmpty && (
						<Box my={1}>
							<Pagination
								onChange={(ev, page) => paginate(page)}
								color="primary"
								size="small"
								shape="rounded"
								page={page}
								count={numPages}
							/>
						</Box>
					)}
				</>
				<AchievementToolbar
					selected={selected}
					selectedIds={selectedIds}
					handleClear={handleClear}
					totalSelected={totalSelected}
				/>
			</Container>
		</>
	);
};

export default AchievementList;
